import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Building2, FolderKanban, Loader2, Package, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useCompanyList } from "@/modules/companies/hooks/useCompanies";
import { useProductList } from "@/modules/products/hooks/useProducts";
import { useProjectList } from "@/modules/projects/hooks/useProjects";
import { useUserList } from "@/modules/users/hooks/useUsers";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { useCreateMeeting } from "../hooks/useMeetings";
import type { MeetingType, MeetingStatus, MeetingListItem } from "../types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onCreated?: (code: string) => void;
  /** When set, the drawer opens in "Duplicate" mode: sticky context is copied
   *  from this record while event-specific fields (date/time/location/status/
   *  attendees) are cleared. Submitting creates a brand-new meeting. */
  duplicateFrom?: MeetingListItem;
};

export function NewMeetingDrawer({
  isOpen,
  onClose,
  onCreated,
  duplicateFrom,
}: Props) {
  const { user } = useAuth();
  const createMeeting = useCreateMeeting();

  const [title, setTitle] = useState("");
  const [type, setType] = useState<MeetingType>("INTERNAL");
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState<MeetingStatus>("SCHEDULED");
  const [date, setDate] = useState("");
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [location, setLocation] = useState("");
  const [meetingUrl, setMeetingUrl] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedProductId, setSelectedProductId] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState("");
  const [selectedPhaseId, setSelectedPhaseId] = useState<string | null>(null);
  const [selectedStageId, setSelectedStageId] = useState<string | null>(null);
  const [organizerId, setOrganizerId] = useState(user?.id ?? "");
  const [selectedAttendees, setSelectedAttendees] = useState<string[]>([]);

  useEffect(() => {
    if (user?.id && !organizerId) setOrganizerId(user.id);
  }, [user?.id, organizerId]);

  const { data: companiesResult } = useCompanyList({ page: 1, limit: 100, operationalOnly: true });
  const companies = companiesResult?.data ?? [];

  const { data: productsResult } = useProductList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
  });
  const products = productsResult?.data ?? [];

  const { data: projectsResult } = useProjectList({
    page: 1,
    limit: 100,
    companyId: selectedCompanyId || undefined,
    productId: selectedProductId || undefined,
  });
  const projects = projectsResult?.data ?? [];

  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  useEffect(() => {
    if (selectedProductId) {
      const product = products.find((p) => p.id === selectedProductId);
      if (product?.companyId) setSelectedCompanyId(product.companyId);
    }
  }, [selectedProductId, products]);

  // Duplicate mode: copy sticky context, clear event-specific fields.
  useEffect(() => {
    if (!isOpen || !duplicateFrom) return;
    const d = duplicateFrom;
    setTitle(d.title ?? "");
    setType(d.type);
    setCategory(d.category ?? "");
    setStatus("SCHEDULED");
    setSelectedCompanyId(d.companyId ?? "");
    setSelectedProductId(d.productId ?? "");
    setSelectedProjectId(d.projectId ?? "");
    setSelectedPhaseId(d.phaseId ?? null);
    setSelectedStageId(d.stageId ?? null);
    setOrganizerId(d.organizerId ?? user?.id ?? "");
    // Cleared — each occurrence gets its own schedule/notes:
    setDate("");
    setStartTime("");
    setEndTime("");
    setLocation("");
    setMeetingUrl("");
    setSelectedAttendees([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, duplicateFrom]);

  function reset() {
    setTitle("");
    setType("INTERNAL");
    setCategory("");
    setStatus("SCHEDULED");
    setDate("");
    setStartTime("");
    setEndTime("");
    setLocation("");
    setMeetingUrl("");
    setSelectedCompanyId("");
    setSelectedProductId("");
    setSelectedProjectId("");
    setSelectedPhaseId(null);
    setSelectedStageId(null);
    setOrganizerId(user?.id ?? "");
    setSelectedAttendees([]);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!title.trim() || !date || !startTime || !organizerId) return;

    const scheduledAt = new Date(`${date}T${startTime}`).toISOString();
    const endedAt = endTime ? new Date(`${date}T${endTime}`).toISOString() : undefined;

    const res = await createMeeting.mutateAsync({
      title: title.trim(),
      type,
      category: category.trim() || undefined,
      status,
      scheduledAt,
      endedAt,
      location: location.trim() || undefined,
      meetingUrl: meetingUrl.trim() || undefined,
      companyId: selectedCompanyId || undefined,
      productId: selectedProductId || undefined,
      projectId: selectedProjectId || undefined,
      phaseId: selectedPhaseId ?? undefined,
      stageId: selectedStageId ?? undefined,
      organizerId,
      attendeeIds: selectedAttendees.length > 0 ? selectedAttendees : undefined,
    });
    onCreated?.(res.meeting.code);
    reset();
    onClose();
  }

  function toggleAttendee(userId: string) {
    setSelectedAttendees((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId],
    );
  }

  if (!isOpen) return null;

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div
        className="fixed right-0 top-0 z-[9999] flex h-full w-[500px] flex-col border-l bg-white shadow-xl"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center justify-between border-b px-6 py-4"
          style={{ borderColor: "hsl(210,30%,85%)" }}
        >
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {duplicateFrom ? "Duplicate Meeting" : "New Meeting"}
            </h2>
            {duplicateFrom && (
              <p className="mt-0.5 text-xs text-muted-foreground">
                Duplicating from {duplicateFrom.code} — {duplicateFrom.title}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
          >
            <X className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-y-auto">
          <div className="flex-1 space-y-6 p-6">
            {/* Meeting Details */}
            <h3 className="text-sm font-semibold text-foreground">Meeting Details</h3>

            <div className="space-y-2">
              <Label htmlFor="mtg-title">
                Title <span className="text-destructive">*</span>
              </Label>
              <Input
                id="mtg-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Enter meeting title"
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mtg-type">
                  Type <span className="text-destructive">*</span>
                </Label>
                <Select
                  id="mtg-type"
                  value={type}
                  onChange={(e) => setType(e.target.value as MeetingType)}
                >
                  <option value="INTERNAL">Internal</option>
                  <option value="EXTERNAL">External</option>
                  <option value="CLIENT">Client</option>
                  <option value="PARTNER">Partner</option>
                  <option value="BOARD">Board</option>
                  <option value="STANDUP">Stand-up</option>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="mtg-category">Category</Label>
                <Input
                  id="mtg-category"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  placeholder="e.g. Kick-off, Review"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mtg-status">Status</Label>
              <Select
                id="mtg-status"
                value={status}
                onChange={(e) => setStatus(e.target.value as MeetingStatus)}
              >
                <option value="SCHEDULED">Scheduled</option>
                <option value="IN_PROGRESS">In Progress</option>
                <option value="COMPLETED">Completed</option>
                <option value="CANCELLED">Cancelled</option>
              </Select>
            </div>

            {/* Schedule */}
            <h3 className="text-sm font-semibold text-foreground">Schedule</h3>

            <div className="space-y-2">
              <Label htmlFor="mtg-date">
                Date <span className="text-destructive">*</span>
              </Label>
              <Input
                id="mtg-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mtg-start">
                  Start Time <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="mtg-start"
                  type="time"
                  value={startTime}
                  onChange={(e) => setStartTime(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mtg-end">End Time</Label>
                <Input
                  id="mtg-end"
                  type="time"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="mtg-location">Location</Label>
                <Input
                  id="mtg-location"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="Room / Address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="mtg-url">Meeting URL</Label>
                <Input
                  id="mtg-url"
                  value={meetingUrl}
                  onChange={(e) => setMeetingUrl(e.target.value)}
                  placeholder="Link to meeting"
                />
              </div>
            </div>

            {/* Association */}
            <h3 className="text-sm font-semibold text-foreground">Association</h3>

            <div className="space-y-2">
              <Label htmlFor="mtg-company">Company</Label>
              <div className="relative">
                <Building2
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="mtg-company"
                  value={selectedCompanyId}
                  onChange={(e) => {
                    setSelectedCompanyId(e.target.value);
                    setSelectedProductId("");
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">Select a company...</option>
                  {companies.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} ({c.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mtg-product">Product</Label>
              <div className="relative">
                <Package
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="mtg-product"
                  value={selectedProductId}
                  onChange={(e) => {
                    setSelectedProductId(e.target.value);
                    setSelectedProjectId("");
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No product</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mtg-project">Project</Label>
              <div className="relative">
                <FolderKanban
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  strokeWidth={1.5}
                />
                <select
                  id="mtg-project"
                  value={selectedProjectId}
                  onChange={(e) => {
                    setSelectedProjectId(e.target.value);
                    setSelectedPhaseId(null);
                    setSelectedStageId(null);
                  }}
                  className="flex h-9 w-full rounded-md border border-border bg-background pl-9 pr-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                >
                  <option value="">No project</option>
                  {projects.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.code})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <PhaseStagePicker
              projectId={selectedProjectId || null}
              phaseId={selectedPhaseId}
              stageId={selectedStageId}
              onPhaseChange={setSelectedPhaseId}
              onStageChange={setSelectedStageId}
            />

            {/* Organizer */}
            <h3 className="text-sm font-semibold text-foreground">Organizer</h3>

            <div className="space-y-2">
              <Label htmlFor="mtg-organizer">
                Organizer <span className="text-destructive">*</span>
              </Label>
              <select
                id="mtg-organizer"
                value={organizerId}
                onChange={(e) => setOrganizerId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <option value="">Select organizer...</option>
                {allUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Attendees */}
            <h3 className="text-sm font-semibold text-foreground">Attendees</h3>

            <div className="space-y-2">
              <div className="flex flex-wrap gap-2">
                {selectedAttendees.map((uid) => {
                  const u = allUsers.find((x) => x.id === uid);
                  return (
                    <span
                      key={uid}
                      className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium"
                      style={{
                        background: "hsl(210,55%,92%)",
                        color: "hsl(210,100%,28%)",
                      }}
                    >
                      {u?.name ?? uid}
                      <button
                        type="button"
                        onClick={() => toggleAttendee(uid)}
                        className="ml-0.5 hover:text-destructive"
                      >
                        <X className="h-3 w-3" strokeWidth={2} />
                      </button>
                    </span>
                  );
                })}
              </div>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) toggleAttendee(e.target.value);
                }}
                className="flex h-9 w-full rounded-md border border-border bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
              >
                <option value="">Add attendee...</option>
                {allUsers
                  .filter((u) => !selectedAttendees.includes(u.id))
                  .map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {/* Footer */}
          <div
            className="flex items-center justify-end gap-3 border-t px-6 py-4"
            style={{ borderColor: "hsl(210,30%,85%)" }}
          >
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={
                !title.trim() || !date || !startTime || !organizerId || createMeeting.isPending
              }
            >
              {createMeeting.isPending && (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" strokeWidth={1.5} />
              )}
              Create Meeting
            </Button>
          </div>
        </form>
      </div>
    </>,
    document.body,
  );
}
